import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import TrailPhotoGallery from '../../components/ui/TrailPhotoGallery';
import TrailSidebar from './components/TrailSidebar';
import TrailContent from './components/TrailContent';
import TrailMap from './components/TrailMap';
import ConnectedPoints from './components/ConnectedPoints';
import StaticMapModal from '../../components/ui/StaticMapModal';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';

const TrailDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isStaticMapOpen, setIsStaticMapOpen] = useState(false);

  // Get trail ID from URL params or default to first trail
  const trailId = searchParams?.get('id') || 'zivot-kroz-vekove';

  // Updated trail data with static map URLs for all 8 trails
  const trailsData = {
    // Serbia Trails
    'zivot-kroz-vekove': {
      id: 'zivot-kroz-vekove',
      name: 'Život kroz vekove (Life Through Centuries)',
      country: 'serbia',
      length: '25 km',
      elevation: '650m',
      duration: '2-3 days',
      difficulty: 'Moderate',
      theme: 'Cultural Heritage & Traditional Life',
      startPoint: 'Rudno, Serbia',
      endPoint: 'Sources of Izubre River',
      bestSeason: 'April to October',
      accessibility: 'Partially accessible, some steep sections',
      coordinates: { lat: 43.4144, lng: 20.4844 },
      staticMapUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_14cd9ae22-1763463087875.png",
      description: `The "Life Through Centuries" trail offers a unique journey through time, showcasing how mountain communities have adapted and thrived across different historical periods. This carefully curated route connects traditional settlements, historic sites, and natural landmarks that tell the story of resilient mountain life in Serbia.`
      // ... rest of trail properties
    },
    'damari-srbije': {
      id: 'damari-srbije',
      name: 'Osluškujući damare srednjovekovne Srbije (Heartbeats of Medieval Serbia)',
      country: 'serbia',
      length: '42 km',
      elevation: '850m',
      duration: '3-4 days',
      difficulty: 'Moderate to Challenging',
      theme: 'Medieval Heritage & Monasteries',
      startPoint: 'Studenica Monastery',
      endPoint: 'Sopoćani Monastery',
      bestSeason: 'April to October',
      accessibility: 'Good paths, some steep monastery approaches',
      coordinates: { lat: 43.4853, lng: 20.5319 },
      staticMapUrl: "https://images.unsplash.com/photo-1533350636407-6e1a62f05ea4",
      description: `This trail connects the most important medieval Serbian monasteries, offering insight into the golden age of Serbian culture and spirituality. Walk in the footsteps of medieval rulers and monks who shaped Serbian identity.`
      // ... rest of trail properties
    },
    'i-bi-voda': {
      id: 'i-bi-voda',
      name: '…I bi voda (…And There Was Water)',
      country: 'serbia',
      length: '18 km',
      elevation: '420m',
      duration: '1-2 days',
      difficulty: 'Easy to Moderate',
      theme: 'Water Heritage & Traditional Life',
      startPoint: 'Srnjača Village',
      endPoint: 'Bzovik Traditional Household',
      bestSeason: 'May to September',
      accessibility: 'Well-marked paths, family-friendly',
      coordinates: { lat: 43.4097, lng: 20.4500 },
      staticMapUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffdf1b50-1763463086687.png",
      description: `Discover how water has shaped mountain communities, from ancient mills to modern eco-tourism. This trail showcases the intimate relationship between people and water resources in rural Serbia.`
      // ... rest of trail properties
    },
    'bio-jednom-jedan': {
      id: 'bio-jednom-jedan',
      name: 'Bio jednom jedan (Once Upon a Time)',
      country: 'serbia',
      length: '35 km',
      elevation: '950m',
      duration: '2-3 days',
      difficulty: 'Challenging',
      theme: 'Myths, Legends & Folklore',
      startPoint: 'Kumanica Village',
      endPoint: 'Jankov Kamen Peak',
      bestSeason: 'June to September',
      accessibility: 'Mountain hiking experience recommended',
      coordinates: { lat: 43.4842, lng: 20.3411 },
      staticMapUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_19e6c3b32-1763463089365.png",
      description: `Journey through landscapes rich in Serbian folklore and mythology. This trail brings ancient stories to life as you explore legendary sites and hear tales passed down through generations.`
      // ... rest of trail properties
    },
    // Montenegro Trails
    'od-glecera-do-grebaje': {
      id: 'od-glecera-do-grebaje',
      name: 'Od glečera do Grebaje (From Glacier to Grebaje)',
      country: 'montenegro',
      length: '55 km',
      elevation: '1,200m',
      duration: '4-5 days',
      difficulty: 'Challenging',
      theme: 'Alpine Heritage & Glacial landscapes',
      startPoint: 'Ropojana Valley',
      endPoint: 'Redžepagić Tower',
      bestSeason: 'June to September',
      accessibility: 'Mountain hiking experience required',
      coordinates: { lat: 42.5097, lng: 19.8336 },
      staticMapUrl: "https://images.unsplash.com/photo-1707234287177-39914cab66f0",
      description: `Experience the dramatic transition from glacial valleys to traditional highland settlements. This challenging trail showcases Montenegro's most spectacular mountain landscapes and rich alpine heritage.`
      // ... rest of trail properties
    },
    'legende-brskova': {
      id: 'legende-brskova',
      name: 'Legende i priče iz srednjovjekovnog Brskova (Legends of Brskovo)',
      country: 'montenegro',
      length: '48 km',
      elevation: '980m',
      duration: '3-4 days',
      difficulty: 'Moderate to Challenging',
      theme: 'Medieval Mining Heritage',
      startPoint: 'Bjelasica Mountain',
      endPoint: 'Đurđevi Stupovi Monastery',
      bestSeason: 'May to October',
      accessibility: 'Good mountain paths, some technical sections',
      coordinates: { lat: 42.8997, lng: 19.5975 },
      staticMapUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1aa76ab87-1763463087196.png",
      description: `Explore the legends surrounding medieval Brskovo, once a thriving mining town. This trail connects historic sites with stunning natural beauty in northern Montenegro.`
      // ... rest of trail properties
    },
    'kanjon-tare': {
      id: 'kanjon-tare',
      name: 'Kanjon Tare (Tara Canyon)',
      country: 'montenegro',
      length: '32 km',
      elevation: '680m',
      duration: '2-3 days',
      difficulty: 'Moderate',
      theme: 'River Heritage & Canyon Landscapes',
      startPoint: 'Tara River Canyon',
      endPoint: 'Đurđeviće Tara Monastery',
      bestSeason: 'April to October',
      accessibility: 'River and canyon paths, some steep sections',
      coordinates: { lat: 43.1278, lng: 19.3118 },
      staticMapUrl: "https://images.unsplash.com/photo-1634209329198-92540a9e08fa",
      description: `Follow Europe's deepest canyon and discover the stories of people who lived along the mighty Tara River. Experience rafting, monasteries, and breathtaking canyon views.`
      // ... rest of trail properties
    },
    'zagrljaj-sume': {
      id: 'zagrljaj-sume',
      name: 'U zagrljaju šume i tri jezera (Embrace of the Forest)',
      country: 'montenegro',
      length: '28 km',
      elevation: '780m',
      duration: '2 days',
      difficulty: 'Moderate',
      theme: 'Forest Heritage & Glacial Lakes',
      startPoint: 'Barno Lake',
      endPoint: 'Durmitor Natural History Museum',
      bestSeason: 'June to September',
      accessibility: 'Mountain forest paths, moderate difficulty',
      coordinates: { lat: 43.1575, lng: 19.0927 },
      staticMapUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_15f5449ba-1763463089379.png",
      description: `Immerse yourself in pristine mountain forests and discover three spectacular glacial lakes. This trail showcases Montenegro's wilderness and traditional forest communities.`
      // ... rest of trail properties
    }
  };

  const currentTrail = trailsData?.[trailId] || trailsData?.['zivot-kroz-vekove'];

  // Mock connected points data
  const connectedPoints = [
  {
    id: 1,
    name: 'Studenica Monastery',
    type: 'cultural',
    description: 'UNESCO World Heritage medieval monastery',
    fullDescription: 'Founded in the 12th century by Stefan Nemanja, Studenica Monastery is the mother church of all Serbian monasteries and a UNESCO World Heritage site. The monastery complex features stunning Byzantine frescoes and represents the pinnacle of medieval Serbian architecture.',
    location: 'Kraljevo, Serbia',
    coordinates: { lat: 43.4859, lng: 20.5294 },
    image: "https://images.unsplash.com/photo-1601575930200-8fb42cde2b39",
    imageAlt: 'Ancient stone monastery with white walls and red tile roof surrounded by green hills'
  },
  {
    id: 2,
    name: 'Kopaonik National Park',
    type: 'natural',
    description: 'Protected mountain ecosystem with diverse flora',
    fullDescription: 'Kopaonik National Park encompasses the highest mountain range in Serbia, featuring subalpine and alpine ecosystems. The park is home to over 1,600 plant species, including many endemic varieties, and provides habitat for wolves, bears, and golden eagles.',
    location: 'Central Serbia',
    coordinates: { lat: 43.2981, lng: 20.8176 },
    image: "https://images.unsplash.com/photo-1633065016461-ad6328699d0a",
    imageAlt: 'Panoramic view of rolling green mountains with dense forest coverage under blue sky'
  },
  {
    id: 3,
    name: 'Heritage Guesthouse Nemanja',
    type: 'tourism',
    description: 'Traditional accommodation with local cuisine',
    fullDescription: 'A family-run guesthouse offering authentic Serbian hospitality in a restored 19th-century house. Guests can enjoy traditional meals prepared with organic ingredients from the family garden and participate in local cultural activities.',
    location: 'Brus, Serbia',
    coordinates: { lat: 43.3897, lng: 20.9833 },
    image: "https://images.unsplash.com/photo-1720858350759-73dbb9c5993f",
    imageAlt: 'Rustic wooden guesthouse with traditional Serbian architecture and flower gardens'
  },
  {
    id: 4,
    name: 'Žiča Monastery',
    type: 'cultural',
    description: 'First seat of Serbian Orthodox Church',
    fullDescription: 'Žiča Monastery served as the coronation church of Serbian kings and the first seat of the Serbian Orthodox Church. The monastery features unique red-painted walls and houses priceless medieval frescoes and manuscripts.',
    location: 'Kraljevo, Serbia',
    coordinates: { lat: 43.7047, lng: 20.6486 },
    image: "https://images.unsplash.com/photo-1714418561050-132dd6876ba5",
    imageAlt: 'Medieval monastery with distinctive red brick walls and Byzantine dome architecture'
  },
  {
    id: 5,
    name: 'Artisan Workshop Miloš',
    type: 'tourism',
    description: 'Traditional pottery and woodcarving studio',
    fullDescription: 'Master craftsman Miloš continues centuries-old traditions of Serbian pottery and woodcarving. Visitors can observe the creation process, purchase authentic handmade items, and participate in workshops to learn traditional techniques.',
    location: 'Vrnjačka Banja, Serbia',
    coordinates: { lat: 43.6214, lng: 20.8969 },
    image: "https://images.unsplash.com/photo-1697439300760-4f2691cdf4b5",
    imageAlt: 'Traditional pottery workshop with clay vessels and wooden tools on rustic workbench'
  },
  {
    id: 6,
    name: 'Golija Mountain Reserve',
    type: 'natural',
    description: 'Biosphere reserve with ancient forests',
    fullDescription: 'Golija Mountain is a UNESCO Biosphere Reserve featuring pristine beech and fir forests, some over 300 years old. The area is known for its medicinal plants, crystal-clear springs, and traditional mountain architecture.',
    location: 'Ivanjica, Serbia',
    coordinates: { lat: 43.3833, lng: 20.2833 },
    image: "https://images.unsplash.com/photo-1629430261604-8cad88ac6dab",
    imageAlt: 'Dense ancient forest with tall trees and dappled sunlight filtering through green canopy'
  }];


  // Mock gallery images
  const galleryImages = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1629340961073-b28816173c2b",
    alt: 'Ancient stone monastery with white walls and red tile roof nestled in green valley',
    caption: 'Studenica Monastery - UNESCO World Heritage site and spiritual heart of Serbia'
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1734457954453-8ecb04a5c850",
    alt: 'Panoramic mountain vista with rolling green hills and forest-covered slopes',
    caption: 'Breathtaking views along the monastery trail through central Serbia'
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1701572725701-9444d59bf514",
    alt: 'Medieval monastery interior with ornate Byzantine frescoes and golden iconostasis',
    caption: 'Priceless medieval frescoes showcase the artistic heritage of Serbian monasteries'
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1475505862009-b3d2fd13c91e",
    alt: 'Forest path winding through ancient trees with dappled sunlight',
    caption: 'Peaceful forest paths connect heritage sites through pristine natural landscapes'
  },
  {
    id: 5,
    src: "https://images.unsplash.com/photo-1651918597904-5aa6bcfd7144",
    alt: 'Traditional Serbian village with wooden houses and flower gardens',
    caption: 'Traditional villages along the route preserve authentic Serbian culture and hospitality'
  }];


  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [trailId]);

  const handleViewOnMap = (point = null) => {
    if (point) {
      navigate(`/heritage-interactive-map?point=${point?.id}`);
    } else {
      navigate('/heritage-interactive-map');
    }
  };

  const handleContactProvider = () => {
    navigate('/contact-and-partners');
  };

  const handleBackToTrails = () => {
    navigate('/thematic-trails-overview');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-64 bg-muted rounded"></div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-4">
                  <div className="h-6 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="space-y-4">
                  <div className="h-32 bg-muted rounded"></div>
                  <div className="h-32 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <button
              onClick={() => navigate('/homepage')}
              className="hover:text-foreground transition-colors duration-fast">

              Home
            </button>
            <Icon name="ChevronRight" size={14} />
            <button
              onClick={handleBackToTrails}
              className="hover:text-foreground transition-colors duration-fast">

              Thematic Trails
            </button>
            <Icon name="ChevronRight" size={14} />
            <span className="text-foreground font-medium">{currentTrail?.name}</span>
          </div>

          {/* Back Button */}
          <div className="mb-6">
            <Button
              variant="outline"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={handleBackToTrails}>

              Back to All Trails
            </Button>
          </div>

          {/* Photo Gallery */}
          <div className="mb-8">
            <TrailPhotoGallery
              images={galleryImages}
              title={`${currentTrail?.name} Gallery`} />

          </div>

          {/* New Official Trail Map Section */}
          {currentTrail?.staticMapUrl &&
          <div className="mb-8">
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-heading font-bold text-xl text-foreground mb-2">
                      Official Trail Map
                    </h2>
                    <p className="text-muted-foreground">
                      High-resolution map showing the complete route with waypoints and landmarks
                    </p>
                  </div>
                  <Button
                  variant="outline"
                  iconName="Maximize2"
                  iconPosition="left"
                  onClick={() => setIsStaticMapOpen(true)}>

                    View Full Size
                  </Button>
                </div>
                
                <div
                className="relative bg-muted rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
                onClick={() => setIsStaticMapOpen(true)}>

                  <AppImage
                  src={currentTrail?.staticMapUrl}
                  alt={`Official trail map for ${currentTrail?.name} showing the complete route with waypoints and landmarks`}
                  className="w-full h-auto object-contain max-h-96" />

                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                    <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2">
                      <Icon name="Maximize2" size={16} />
                      <span className="text-sm font-medium">Click to enlarge</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Info" size={12} className="text-primary-foreground" />
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Trail Map Features:</p>
                    <ul className="space-y-0.5">
                      <li>• Complete route with elevation profiles</li>
                      <li>• Marked waypoints and points of interest</li>
                      <li>• Accommodation and service locations</li>
                      <li>• Emergency contact information</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          }

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <TrailContent trailData={currentTrail} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <TrailSidebar
                  trailData={currentTrail}
                  onViewOnMap={() => handleViewOnMap()}
                  onContactProvider={handleContactProvider} />

              </div>
            </div>
          </div>

          {/* Interactive Map */}
          <div className="mb-8">
            <TrailMap
              trailData={currentTrail}
              connectedPoints={connectedPoints}
              onPointClick={handleViewOnMap} />

          </div>

          {/* Connected Points */}
          <div className="mb-8">
            <ConnectedPoints
              points={connectedPoints}
              onViewOnMap={handleViewOnMap} />

          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-primary to-accent rounded-lg p-8 text-center">
            <h2 className="font-heading font-bold text-2xl text-primary-foreground mb-4">
              Ready to Explore This Trail?
            </h2>
            <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
              Plan your journey along the {currentTrail?.name} and discover the rich heritage 
              and natural beauty that awaits. Connect with local guides and accommodations 
              to make the most of your experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                iconName="Map"
                iconPosition="left"
                onClick={() => handleViewOnMap()}>

                Explore Interactive Map
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Building"
                iconPosition="left"
                onClick={() => navigate('/tourism-offerings')}
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">

                Find Accommodation
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Static Map Modal */}
      <StaticMapModal
        isOpen={isStaticMapOpen}
        onClose={() => setIsStaticMapOpen(false)}
        mapUrl={currentTrail?.staticMapUrl}
        trailName={currentTrail?.name}>

        <div className="mb-4">
          <h3 className="font-heading font-medium text-base text-foreground mb-2">
            Trail Information
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Length:</span>
              <p className="font-medium text-foreground">{currentTrail?.length}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Duration:</span>
              <p className="font-medium text-foreground">{currentTrail?.duration}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Difficulty:</span>
              <p className="font-medium text-foreground">{currentTrail?.difficulty}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Elevation:</span>
              <p className="font-medium text-foreground">{currentTrail?.elevation}</p>
            </div>
          </div>
        </div>
      </StaticMapModal>
    </div>);

};

export default TrailDetails;